import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useExerciseHistory } from "../hooks/useExerciseHistory";
import { saveActiveTraining, getActiveTraining, clearActiveTraining } from "../lib/activeTraining";
import {
  ArrowLeft, Check, Timer, Dumbbell, CheckCircle, X, Plus,
  Search, Trash2, TrendingUp, TrendingDown, Activity, Share2, Link, Trophy,
} from "lucide-react";
import {
  LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = "#C9A84C";
const DEFAULT_REST = 90;

function formatTime(sec) {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function formatRestTime(s) {
  const abs = Math.abs(s);
  return `${Math.floor(abs / 60)}:${(abs % 60).toString().padStart(2, "0")}`;
}

// ─── GEDEELDE AUDIO CONTEXT (iOS-compatible singleton) ───────────────────────
let _sharedAudio = null;

function getAudio() {
  if (!_sharedAudio) {
    try {
      _sharedAudio = new (window.AudioContext || window.webkitAudioContext)();
    } catch (e) { return null; }
  }
  return _sharedAudio;
}

function unlockAudio() {
  try {
    const ctx = getAudio();
    if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
  } catch (e) {}
}

function playRestDoneSound() {
  try {
    const ctx = getAudio();
    if (!ctx) return;
    [[0, 660], [0.18, 660], [0.36, 880]].forEach(([delay, freq]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = "sine"; osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.35, ctx.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.14);
      osc.start(ctx.currentTime + delay); osc.stop(ctx.currentTime + delay + 0.14);
    });
  } catch (e) {}
}

// ─── PR MELDING TOAST ─────────────────────────────────────────────────────────
function PrMelding({ melding, onDismiss }) {
  useEffect(() => {
    if (!melding) return;
    const t = setTimeout(onDismiss, 3500);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [melding]);

  if (!melding) return null;

  return (
    <div
      className="fixed top-4 left-0 right-0 z-[300] flex justify-center px-4 pointer-events-none"
      data-testid="pr-melding-toast"
    >
      <button
        onClick={onDismiss}
        className="pointer-events-auto flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-2xl border transition-transform active:scale-95"
        style={{
          background: "#0d0d0d",
          borderColor: "#C9A84C",
          boxShadow: "0 0 50px rgba(201,168,76,0.4)",
        }}
      >
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: "#C9A84C" }}
        >
          <Trophy size={18} className="text-black" />
        </div>
        <div className="text-left">
          <p style={{ color: "#C9A84C" }} className="font-black text-sm leading-tight">
            Nieuw persoonlijk record!
          </p>
          <p className="text-white text-xs mt-0.5 font-semibold">
            {melding.naam} — {melding.kg} kg
          </p>
        </div>
      </button>
    </div>
  );
}

// ─── REST TIMER ───────────────────────────────────────────────────────────────
function RestTimer({ endTime, total, onSkip, onAdd }) {
  const remaining = endTime ? Math.max(0, Math.ceil((endTime - Date.now()) / 1000)) : 0;
  if (!endTime || remaining <= 0) return null;
  const pct = Math.max(0, (remaining / total) * 100);
  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#0a0802] border-t border-[#C9A84C]/25 px-4 pt-4 pb-6">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Timer size={16} className="text-[#C9A84C]" />
            <span className="text-sm font-bold uppercase tracking-widest text-[#C9A84C]">Rusttijd</span>
          </div>
          <div className="flex gap-2">
            <button onClick={() => onAdd(15)} className="text-zinc-400 hover:text-white text-sm font-semibold px-3 py-1 rounded-lg border border-white/8 hover:border-white/20 transition-colors">+15s</button>
            <button onClick={onSkip} className="text-zinc-400 hover:text-white text-sm font-semibold px-3 py-1 rounded-lg border border-white/8 hover:border-white/20 transition-colors">Overslaan</button>
          </div>
        </div>
        <div className="flex items-center gap-5">
          <span className="text-5xl font-bold tabular-nums tracking-tight w-28 text-[#C9A84C]">
            {formatRestTime(remaining)}
          </span>
          <div className="flex-1">
            <div className="h-2.5 bg-white/8 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-1000"
                style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${GOLD}, #D4AF37)` }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── OEFENING ZOEKER MODAL ────────────────────────────────────────────────────
function OefeningZoekerModal({ onAdd, onClose, token, bestaandeNamen }) {
  const [query, setQuery] = useState("");
  const [library, setLibrary] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/exercises`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setLibrary(d.exercises || []))
      .catch(() => {});
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [token]);

  useEffect(() => {
    if (!query.trim()) { setFiltered(library.slice(0, 12)); return; }
    const q = query.toLowerCase();
    setFiltered(library.filter(e => e.naam.toLowerCase().includes(q)).slice(0, 12));
  }, [query, library]);

  const pick = (naam) => {
    if (bestaandeNamen.includes(naam)) return;
    onAdd(naam);
    onClose();
  };

  const addCustom = () => {
    if (!query.trim()) return;
    pick(query.trim());
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg bg-[#111] border border-white/10 rounded-b-3xl pb-8 max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="px-5 pt-5 pb-4 border-b border-white/6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-white font-bold">Oefening toevoegen</p>
            <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
          </div>
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === "Enter" && addCustom()}
              placeholder="Zoek of typ oefening..."
              data-testid="vrije-logger-zoek"
              className="w-full bg-[#1a1a1a] border border-white/8 rounded-xl pl-10 pr-4 py-3 text-white text-base focus:outline-none focus:border-[#C9A84C]/50 transition-colors"
            />
          </div>
        </div>
        <div className="overflow-y-auto flex-1 py-2">
          {query.trim() && (
            <button
              onClick={addCustom}
              data-testid="vrije-logger-voeg-vrij-toe"
              className="w-full flex items-center gap-3 px-5 py-3 hover:bg-[#C9A84C]/8 transition-colors border-b border-white/4"
            >
              <div className="w-8 h-8 rounded-lg bg-[#C9A84C]/15 flex items-center justify-center">
                <Plus size={14} style={{ color: GOLD }} />
              </div>
              <div className="text-left">
                <p className="text-white text-sm font-semibold">"{query.trim()}" toevoegen</p>
                <p className="text-zinc-500 text-xs">Vrije oefening</p>
              </div>
            </button>
          )}
          {filtered.map((ex, fi) => {
            const al = bestaandeNamen.includes(ex.naam);
            return (
              <button
                key={ex.naam + "_" + fi}
                onClick={() => !al && pick(ex.naam)}
                data-testid={`vrije-logger-kies-${ex.naam.toLowerCase().replace(/\s+/g, "-")}`}
                className={`w-full flex items-center gap-3 px-5 py-3 transition-colors text-left ${al ? "opacity-40 cursor-not-allowed" : "hover:bg-white/4"}`}
              >
                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
                  <Dumbbell size={13} className="text-zinc-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-white text-sm font-semibold truncate">{ex.naam}</p>
                  {ex.spiergroep && <p className="text-zinc-500 text-xs">{ex.spiergroep}</p>}
                </div>
                {al && <span className="ml-auto text-zinc-600 text-xs shrink-0">Toegevoegd</span>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── SUPERSET PICKER VOOR VRIJE TRAINING ──────────────────────────────────────
function VrijeSupersetPicker({ token, onSelect, onClose }) {
  const [step, setStep] = useState(1);
  const [ex1, setEx1] = useState(null);
  const [zoek, setZoek] = useState("");
  const [bibliotheek, setBibliotheek] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/exercises`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setBibliotheek(d.exercises || []))
      .catch(() => {});
  }, [token]);

  const gefilterd = bibliotheek.filter(e =>
    !zoek || e.naam.toLowerCase().includes(zoek.toLowerCase()) ||
    (e.spiergroep || "").toLowerCase().includes(zoek.toLowerCase())
  );

  const handleKies = (naam) => {
    if (step === 1) { setEx1(naam); setStep(2); setZoek(""); }
    else { onSelect(ex1, naam); onClose(); }
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex flex-col">
      <div className="flex items-center gap-3 px-4 pt-10 pb-3 border-b border-white/5">
        <button onClick={step === 2 ? () => { setStep(1); setEx1(null); } : onClose}
          className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center">
          <ArrowLeft size={16} className="text-white" />
        </button>
        <div>
          <p className="text-xs font-bold uppercase tracking-widest" style={{ color: GOLD }}>
            {step === 1 ? "Superset — Oefening 1" : "Superset — Oefening 2"}
          </p>
          {step === 2 && <p className="text-zinc-400 text-xs">na: <span className="text-white font-semibold">{ex1}</span></p>}
        </div>
      </div>
      <div className="px-4 py-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input autoFocus value={zoek} onChange={e => setZoek(e.target.value)}
            placeholder="Zoek oefening..."
            className="w-full bg-[#1a1a1a] border border-white/8 rounded-xl pl-9 pr-4 py-3 text-white text-base focus:outline-none focus:border-[#C9A84C]/50 placeholder-zinc-600" />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-4 space-y-1 pb-10">
        {gefilterd.slice(0, 40).map((e, gi) => (
          <button key={e.naam + "_" + gi} onClick={() => handleKies(e.naam)}
            className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 text-left transition-colors">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(201,168,76,0.12)" }}>
              <Dumbbell size={13} style={{ color: GOLD }} />
            </div>
            <div className="min-w-0">
              <p className="text-white text-sm font-semibold truncate">{e.naam}</p>
              {e.spiergroep && <p className="text-zinc-500 text-xs">{e.spiergroep}</p>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── COMPLETION SCREEN ────────────────────────────────────────────────────────
function VrijeCompletionScreen({ elapsed, exercises, sessionPrs, prevData, onAccount }) {
  const [gekopieerd, setGekopieerd] = useState(false);

  const totalVolume = exercises.reduce((s, ex) => {
    if (ex.type === "superset") return s + ex.oefeningen.reduce((s2, sub) =>
      s2 + sub.sets.reduce((s3, set) => s3 + (parseFloat(set.kg) || 0) * (parseInt(set.reps) || 0), 0), 0);
    return s + ex.sets.reduce((s2, set) => s2 + (parseFloat(set.kg) || 0) * (parseInt(set.reps) || 0), 0);
  }, 0);
  const completedSets = exercises.reduce((s, ex) => {
    if (ex.type === "superset") return s + ex.oefeningen.reduce((s2, sub) => s2 + sub.sets.filter(x => x.done).length, 0);
    return s + ex.sets.filter(x => x.done).length;
  }, 0);
  const totalSets = exercises.reduce((s, ex) => {
    if (ex.type === "superset") return s + ex.oefeningen.reduce((s2, sub) => s2 + sub.sets.length, 0);
    return s + ex.sets.length;
  }, 0);

  // Berekent de beste set (max kg) voor een oefening in de huidige sessie
  const getBestSet = (sets) => {
    const done = sets.filter(s => s.done);
    if (!done.length) return null;
    return done.reduce((best, s) => {
      const kg = parseFloat(s.kg) || 0;
      return kg > (parseFloat(best?.kg) || 0) ? s : best;
    }, done[0]);
  }

  const handleDeel = async () => {
    const oefTekst = exercises.slice(0, 6)
      .map(ex => {
        if (ex.type === "superset") {
          const gedaan = ex.oefeningen[0].sets.filter(s => s.done).length;
          return `• Superset: ${ex.oefeningen.map(o => o.naam).join(" + ")} — ${gedaan} rondes`;
        }
        const gedaan = ex.sets.filter(s => s.done).length;
        return `• ${ex.naam} — ${gedaan}/${ex.sets.length} sets`;
      })
      .join("\n");
    const volumeRegel = totalVolume > 0 ? `\n💪 Volume: ${totalVolume.toLocaleString("nl-NL")} kg` : "";
    const tekst = `Vrije training voltooid!\n\nTijd: ${formatTime(elapsed)}${volumeRegel}\nSets: ${completedSets}/${totalSets}\n\nOefeningen:\n${oefTekst}\n\nVia FitSmith`;

    if (navigator.share) {
      try { await navigator.share({ title: "Vrije training voltooid!", text: tekst }); } catch {}
    } else {
      try {
        await navigator.clipboard.writeText(tekst);
        setGekopieerd(true);
        setTimeout(() => setGekopieerd(false), 2500);
      } catch {}
    }
  };

  return (
    <div className="min-h-screen bg-[#080808] pb-10">
      <div className="sticky top-0 z-10 bg-[#080808]/95 backdrop-blur-md border-b border-white/5 px-4 py-3 flex items-center gap-3">
        <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: "rgba(201,168,76,0.15)" }}>
          <CheckCircle size={16} style={{ color: GOLD }} />
        </div>
        <div>
          <p className="text-white font-bold text-sm">Vrije training voltooid!</p>
          <p className="text-zinc-500 text-xs">{exercises.length} oefeningen · {formatTime(elapsed)}</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-3 pt-5 space-y-4">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Tijd", value: formatTime(elapsed) },
            { label: "Volume", value: totalVolume > 0 ? `${totalVolume.toLocaleString("nl-NL")} kg` : "–" },
            { label: "Sets", value: `${completedSets}/${totalSets}` },
          ].map(({ label, value }) => (
            <div key={label} className="rounded-2xl p-4 text-center border border-white/5" style={{ background: "#111" }}>
              <p className="text-2xl font-bold" style={{ color: GOLD }}>{value}</p>
              <p className="text-zinc-500 text-xs mt-1">{label}</p>
            </div>
          ))}
        </div>

        {/* Nieuwe Records kaart */}
        {sessionPrs?.length > 0 && (
          <div
            className="rounded-2xl border overflow-hidden"
            style={{ background: "linear-gradient(135deg, #0f0c02, #0d0a00)", borderColor: "rgba(201,168,76,0.4)" }}
            data-testid="completion-pr-kaart"
          >
            <div
              className="px-4 py-3 flex items-center gap-2 border-b"
              style={{ borderColor: "rgba(201,168,76,0.2)" }}
            >
              <Trophy size={15} style={{ color: GOLD }} />
              <p className="font-black text-sm" style={{ color: GOLD }}>
                {sessionPrs.length === 1
                  ? "1 nieuw persoonlijk record!"
                  : `${sessionPrs.length} nieuwe persoonlijke records!`}
              </p>
            </div>
            <div className="divide-y divide-white/4">
              {sessionPrs.map((pr, i) => (
                <div key={i} className="px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <span
                      className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black text-black shrink-0"
                      style={{ background: i === 0 ? "#FFD700" : i === 1 ? "#C0C0C0" : "#CD7F32" }}
                    >
                      {i + 1}
                    </span>
                    <p className="text-white text-sm font-semibold">{pr.naam}</p>
                  </div>
                  <span className="font-black text-sm tabular-nums" style={{ color: GOLD }}>
                    {pr.kg} kg
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Oefeningen overzicht met voortgang */}
        <div className="rounded-2xl overflow-hidden border border-white/5" style={{ background: "#111" }}>
          <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
            <Dumbbell size={13} style={{ color: GOLD }} />
            <p className="text-white font-bold text-sm">Oefeningen & Voortgang</p>
          </div>
          <div className="divide-y divide-white/4">
            {exercises.map((ex, i) => {
              if (ex.type === "superset") {
                return (
                  <div key={i} className="px-4 py-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Link size={11} style={{ color: GOLD }} />
                      <p className="text-white text-sm font-semibold">Superset</p>
                    </div>
                    {ex.oefeningen.map((sub, si) => {
                      const vol = sub.sets.reduce((s, set) =>
                        s + (parseFloat(set.kg) || 0) * (parseInt(set.reps) || 0), 0);
                      const best = getBestSet(sub.sets);
                      const prev = prevData?.[sub.naam];
                      const prevKg = prev?.kg ?? null;
                      const currKg = best ? parseFloat(best.kg) || 0 : 0;
                      const diff = prevKg !== null && currKg > 0 ? currKg - prevKg : null;
                      return (
                        <div key={si} className="mb-2 ml-4">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-zinc-400 text-xs font-semibold">{sub.naam}</p>
                            {vol > 0 && <span className="text-xs font-bold" style={{ color: GOLD }}>{vol.toLocaleString("nl-NL")} kg</span>}
                          </div>
                          {prevKg !== null && currKg > 0 && (
                            <div className="flex items-center gap-1.5 mb-1">
                              <span className="text-zinc-600 text-[10px]">Vorige beste: {prevKg}kg</span>
                              {diff !== 0 && (
                                <span className={`flex items-center gap-0.5 text-[10px] font-bold ${diff > 0 ? "text-green-400" : "text-red-400"}`}>
                                  {diff > 0 ? <TrendingUp size={9} /> : <TrendingDown size={9} />}
                                  {diff > 0 ? "+" : ""}{diff.toFixed(1).replace(".0", "")}kg
                                </span>
                              )}
                              {diff === 0 && <span className="text-zinc-500 text-[10px]">= zelfde</span>}
                            </div>
                          )}
                          <div className="flex flex-wrap gap-1">
                            {sub.sets.slice(0, 4).map((s, si2) => (
                              <div key={si2} className="px-1.5 py-0.5 rounded text-[10px] font-mono border"
                                style={s.done ? { background: "rgba(201,168,76,0.1)", color: GOLD, borderColor: "rgba(201,168,76,0.2)" } : { background: "#1a1a1a", color: "#52525b", borderColor: "rgba(255,255,255,0.06)" }}>
                                {parseFloat(s.kg) > 0 ? `${s.kg}×${s.reps}` : `×${s.reps || "–"}`}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              }
              const vol = ex.sets.reduce((s, set) =>
                s + (parseFloat(set.kg) || 0) * (parseInt(set.reps) || 0), 0);
              const best = getBestSet(ex.sets);
              const prev = prevData?.[ex.naam];
              const prevKg = prev?.kg ?? null;
              const currKg = best ? parseFloat(best.kg) || 0 : 0;
              const diff = prevKg !== null && currKg > 0 ? currKg - prevKg : null;
              return (
                <div key={i} className="px-4 py-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-white text-sm font-semibold">{ex.naam}</p>
                    {vol > 0 && <span className="text-xs font-bold" style={{ color: GOLD }}>{vol.toLocaleString("nl-NL")} kg</span>}
                  </div>
                  {/* Voortgang t.o.v. vorige keer */}
                  {prevKg !== null && currKg > 0 && (
                    <div className="flex items-center gap-2 mb-2 px-2.5 py-1.5 rounded-lg" style={{ background: "rgba(255,255,255,0.03)" }}>
                      <Activity size={11} className="text-zinc-600 shrink-0" />
                      <span className="text-zinc-500 text-xs">Vorige beste: <span className="text-zinc-300 font-semibold">{prevKg}kg{prev?.reps ? ` × ${prev.reps}` : ""}</span></span>
                      {diff !== null && diff !== 0 && (
                        <span className={`ml-auto flex items-center gap-0.5 text-xs font-black ${diff > 0 ? "text-green-400" : "text-red-400"}`}>
                          {diff > 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                          {diff > 0 ? "+" : ""}{diff.toFixed(1).replace(".0", "")}kg
                        </span>
                      )}
                      {diff === 0 && <span className="ml-auto text-zinc-500 text-xs">= zelfde</span>}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {ex.sets.map((s, si) => (
                      <div key={si} className="px-2.5 py-1 rounded-lg text-xs font-mono border"
                        style={s.done
                          ? { background: "rgba(201,168,76,0.1)", color: GOLD, borderColor: "rgba(201,168,76,0.2)" }
                          : { background: "#1a1a1a", color: "#52525b", borderColor: "rgba(255,255,255,0.06)" }}>
                        {parseFloat(s.kg) > 0 ? `${s.kg} kg × ${s.reps}` : `× ${s.reps || "–"}`}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Deel resultaat knop */}
        <button
          onClick={handleDeel}
          data-testid="vrije-completion-deel"
          className="w-full py-3.5 rounded-2xl font-bold text-sm border border-white/10 text-zinc-400 hover:border-[#C9A84C]/40 hover:text-[#C9A84C] transition-all flex items-center justify-center gap-2"
        >
          <Share2 size={16} />
          {gekopieerd ? "Tekst gekopieerd!" : "Deel resultaat"}
        </button>

        <button
          onClick={onAccount}
          data-testid="vrije-completion-naar-account"
          className="w-full py-4 rounded-2xl font-black text-base text-black"
          style={{ background: `linear-gradient(135deg, ${GOLD}, #D4AF37)` }}
        >
          Terug naar account
        </button>
      </div>
    </div>
  );
}

// ─── HOOFD PAGINA ─────────────────────────────────────────────────────────────
export default function VrijeTrainingLogger() {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const token = authUser?.token;

  // Toegangscheck: voor zowel directe users als bedrijfssporters
  const [toegangChecked, setToeganChecked] = useState(false);
  const [toegangBlokkeerd, setToeganBlokkeerd] = useState(false);

  useEffect(() => {
    const role = authUser?.role;
    if (!role || role === "company_admin" || role === "admin") {
      setToeganChecked(true);
      return;
    }
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/me/vrije-training-access`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && d.enabled === false) setToeganBlokkeerd(true); })
      .catch(() => {})
      .finally(() => setToeganChecked(true));
  }, [authUser?.role, token]);

  const savedTrainingRef = useRef((() => {
    const s = getActiveTraining();
    return s && s.route === "/vrije-training" ? s : null;
  })());

  const [naam, setNaam] = useState(() => {
    if (savedTrainingRef.current?.state?.naam) return savedTrainingRef.current.state.naam;
    const d = new Date();
    return `Vrije training ${d.toLocaleDateString("nl-NL", { day: "numeric", month: "long" })}`;
  });
  const [oefeningen, setOefeningen] = useState(() => savedTrainingRef.current?.state?.oefeningen || []); // [{naam, sets:[{reps,kg,done}]}] — nieuwe vrije training start altijd leeg
  const [restTimer, setRestTimer] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [finished, setFinished] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [showZoeker, setShowZoeker] = useState(false);
  const [showSupersetPicker, setShowSupersetPicker] = useState(false);
  const [rustPerEx, setRustPerEx] = useState(() => savedTrainingRef.current?.state?.rustPerEx || {});
  // Globale oefening-geschiedenis: beste gewicht per oefening over alle trainingen
  const prevData = useExerciseHistory(token);
  const prevDataRef = useRef(prevData);
  useEffect(() => { prevDataRef.current = prevData; }, [prevData]);
  const sessionMaxKg = useRef({});
  const [prMelding, setPrMelding] = useState(null);
  const [sessionPrs, setSessionPrs] = useState([]);
  const startTimeRef = useRef(savedTrainingRef.current?.startedAt || Date.now());

  // Verstreken tijd bijhouden
  useEffect(() => {
    const iv = setInterval(() => setElapsed(Math.round((Date.now() - startTimeRef.current) / 1000)), 1000);
    return () => clearInterval(iv);
  }, []);

  // Bewaar actieve training zodat de sporter kan wegnavigeren en terugkeren
  useEffect(() => {
    if (finished) return;
    if (oefeningen.length === 0) {
      const cur = getActiveTraining();
      if (cur?.route === "/vrije-training") clearActiveTraining();
      return;
    }
    saveActiveTraining("/vrije-training", naam, startTimeRef.current, { naam, oefeningen, rustPerEx });
  }, [naam, oefeningen, rustPerEx, finished]);

  const [, forceTimerRender] = useState(0);

  // Rest timer — loopt door na terugkeer naar de pagina
  useEffect(() => {
    if (!restTimer) return;
    const tick = () => {
      const remaining = Math.ceil((restTimer.endTime - Date.now()) / 1000);
      if (remaining <= 0) {
        playRestDoneSound();
        setRestTimer(null);
      } else {
        forceTimerRender(n => n + 1);
      }
    };
    tick();
    const iv = setInterval(tick, 500);
    const onVisible = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [restTimer]);

  const addOefening = useCallback((naam) => {
    setOefeningen(prev => [...prev, { naam, sets: [{ reps: 10, kg: "", done: false }] }]);
  }, []);

  const addSuperset = useCallback((naam1, naam2) => {
    setOefeningen(prev => [...prev, {
      type: "superset",
      naam: `Superset ${prev.filter(e => e.type === "superset").length + 1}`,
      oefeningen: [
        { naam: naam1, sets: [{ reps: 10, kg: "", done: false }] },
        { naam: naam2, sets: [{ reps: 10, kg: "", done: false }] },
      ],
    }]);
  }, []);

  const updateSupersetSet = useCallback((exIdx, subIdx, setIdx, field, value) => {
    setOefeningen(prev => {
      const updated = [...prev];
      const ex = { ...updated[exIdx] };
      const subList = [...ex.oefeningen];
      const sub = { ...subList[subIdx] };
      const subSets = [...sub.sets];
      subSets[setIdx] = { ...subSets[setIdx], [field]: value };
      sub.sets = subSets;
      subList[subIdx] = sub;
      ex.oefeningen = subList;
      updated[exIdx] = ex;
      return updated;
    });
  }, []);

  const toggleSupersetRound = useCallback((exIdx, numSubEx, setIdx, rustSec) => {
    unlockAudio(); // iOS: ontgrendel AudioContext bij gebruikersinteractie
    setOefeningen(prev => {
      const allDoneNow = prev[exIdx].oefeningen.every(sub => sub.sets[setIdx]?.done);
      const updated = [...prev];
      const ex = { ...updated[exIdx] };
      ex.oefeningen = ex.oefeningen.map(sub => {
        const subSets = [...sub.sets];
        if (subSets[setIdx]) subSets[setIdx] = { ...subSets[setIdx], done: !allDoneNow };
        return { ...sub, sets: subSets };
      });
      updated[exIdx] = ex;
      if (!allDoneNow && rustSec > 0) {
        setTimeout(() => setRestTimer({ endTime: Date.now() + rustSec * 1000, total: rustSec }), 0);
      }
      return updated;
    });
  }, []);

  const addSupersetSet = useCallback((exIdx) => {
    setOefeningen(prev => {
      const updated = [...prev];
      const ex = { ...updated[exIdx] };
      ex.oefeningen = ex.oefeningen.map(sub => {
        const last = sub.sets[sub.sets.length - 1];
        return { ...sub, sets: [...sub.sets, { reps: last?.reps || 10, kg: last?.kg || "", done: false }] };
      });
      updated[exIdx] = ex;
      return updated;
    });
  }, []);

  const removeOefening = useCallback((i) => {
    setOefeningen(prev => prev.filter((_, idx) => idx !== i));
  }, []);

  const updateSet = useCallback((exIdx, setIdx, field, value) => {
    setOefeningen(prev => {
      const updated = [...prev];
      const sets = [...updated[exIdx].sets];
      sets[setIdx] = { ...sets[setIdx], [field]: value };
      updated[exIdx] = { ...updated[exIdx], sets };
      return updated;
    });
  }, []);

  const toggleSet = useCallback((exIdx, setIdx, rustSec, exerciseName, newKg) => {
    unlockAudio(); // iOS: ontgrendel AudioContext bij gebruikersinteractie
    setOefeningen(prev => {
      const updated = [...prev];
      const sets = [...updated[exIdx].sets];
      const wasDone = sets[setIdx].done;
      sets[setIdx] = { ...sets[setIdx], done: !wasDone };
      updated[exIdx] = { ...updated[exIdx], sets };
      if (!wasDone) {
        if (setIdx < sets.length - 1 && rustSec > 0) {
          setRestTimer({ endTime: Date.now() + rustSec * 1000, total: rustSec });
        }
        // PR check
        if (exerciseName && newKg > 0) {
          const histMax = prevDataRef.current[exerciseName]?.kg || 0;
          const sesMax  = sessionMaxKg.current[exerciseName] || 0;
          const prevMax = Math.max(histMax, sesMax);
          if (newKg > prevMax) {
            setPrMelding({ naam: exerciseName, kg: newKg });
            setSessionPrs(prev => {
              const idx = prev.findIndex(p => p.naam === exerciseName);
              if (idx >= 0) {
                const updated = [...prev];
                updated[idx] = { naam: exerciseName, kg: newKg };
                return updated;
              }
              return [...prev, { naam: exerciseName, kg: newKg }];
            });
          }
          if (newKg > sesMax) {
            sessionMaxKg.current[exerciseName] = newKg;
          }
        }
      }
      return updated;
    });
  }, []);

  const addSet = useCallback((exIdx) => {
    setOefeningen(prev => {
      const updated = [...prev];
      const lastSet = updated[exIdx].sets[updated[exIdx].sets.length - 1];
      updated[exIdx] = {
        ...updated[exIdx],
        sets: [...updated[exIdx].sets, { reps: lastSet?.reps || 10, kg: lastSet?.kg || "", done: false }],
      };
      return updated;
    });
  }, []);

  const removeSet = useCallback((exIdx, setIdx) => {
    setOefeningen(prev => {
      const updated = [...prev];
      if (updated[exIdx].sets.length <= 1) return prev;
      updated[exIdx] = { ...updated[exIdx], sets: updated[exIdx].sets.filter((_, i) => i !== setIdx) };
      return updated;
    });
  }, []);

  const handleWeightBlur = useCallback((exIdx, setIdx, value) => {
    if (!value || !String(value).trim()) return;
    setOefeningen(prev => {
      const updated = [...prev];
      const sets = [...updated[exIdx].sets];
      let changed = false;
      for (let i = setIdx + 1; i < sets.length; i++) {
        if (!sets[i].kg || String(sets[i].kg).trim() === "") {
          sets[i] = { ...sets[i], kg: value };
          changed = true;
        }
      }
      if (changed) updated[exIdx] = { ...updated[exIdx], sets };
      return updated;
    });
  }, []);

  const handleFinish = async () => {
    if (saving || oefeningen.length === 0) return;
    setSaving(true);
    setSaveError(false);
    const durationSeconds = Math.round((Date.now() - startTimeRef.current) / 1000);
    setElapsed(durationSeconds);
    const sessionId = `vrije_${Date.now()}`;
    const body = {
      session_id: sessionId,
      session_naam: naam,
      training_naam: naam,
      duration_seconds: durationSeconds,
      exercises: oefeningen.flatMap((ex, exIdx) => {
        if (ex.type === "superset") {
          return ex.oefeningen.map((sub, si) => ({
            code: sub.naam.toLowerCase().replace(/\s+/g, "_"),
            naam: sub.naam,
            superset_groep: String(exIdx),
            sets: sub.sets.map(s => ({
              kg: s.kg ? parseFloat(s.kg) || null : null,
              reps: s.reps || null,
              completed: s.done,
            })),
          }));
        }
        return [{
          code: ex.naam.toLowerCase().replace(/\s+/g, "_"),
          naam: ex.naam,
          sets: ex.sets.map(s => ({
            kg: s.kg ? parseFloat(s.kg) || null : null,
            reps: s.reps || null,
            completed: s.done,
          })),
        }];
      }),
    };
    try {
      const r = await fetch(`${API}/api/workout/vrije_training`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) { setSaveError(true); setSaving(false); return; }
      clearActiveTraining();
      setFinished(true);
    } catch { setSaveError(true); }
    setSaving(false);
  };

  const allDone = oefeningen.length > 0 && oefeningen.every(ex => {
    if (ex.type === "superset") return ex.oefeningen.every(sub => sub.sets.every(s => s.done));
    return ex.sets.every(s => s.done);
  });
  const resterendeSets = oefeningen.reduce((s, ex) => {
    if (ex.type === "superset") return s + ex.oefeningen.reduce((s2, sub) => s2 + sub.sets.filter(x => !x.done).length, 0);
    return s + ex.sets.filter(x => !x.done).length;
  }, 0);

  if (finished) return (
    <VrijeCompletionScreen
      elapsed={elapsed}
      exercises={oefeningen}
      sessionPrs={sessionPrs}
      prevData={prevData}
      onAccount={() => navigate("/account")}
    />
  );

  // Toegangscheck: sporter mag geen vrije training doen als de company het uitgeschakeld heeft
  if (!toegangChecked) return null;
  if (toegangBlokkeerd) return (
    <div className="min-h-screen bg-[#080808] flex flex-col items-center justify-center px-6 text-center">
      <div className="w-16 h-16 rounded-2xl bg-zinc-800 border border-white/8 flex items-center justify-center mb-5">
        <Dumbbell size={28} className="text-zinc-600" />
      </div>
      <h2 className="text-white font-bold text-xl mb-2">Vrije Training niet beschikbaar</h2>
      <p className="text-zinc-400 text-sm max-w-xs mb-8">
        Vrije Training is uitgeschakeld door je coach. Neem contact op om toegang te vragen.
      </p>
      <button
        onClick={() => navigate("/account")}
        className="px-6 py-3 rounded-xl text-black font-bold text-sm"
        style={{ background: "#C9A84C" }}
        data-testid="vrije-training-blocked-back"
      >
        Terug naar account
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#080808] pb-36">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-[#080808]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate("/account")}
            data-testid="vrije-logger-terug"
            className="w-9 h-9 rounded-xl bg-[#141414] border border-white/6 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={17} />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest">Vrije Training</p>
            <input
              value={naam}
              onChange={e => setNaam(e.target.value)}
              data-testid="vrije-logger-naam"
              className="bg-transparent text-white font-bold text-sm focus:outline-none w-full truncate"
            />
          </div>
          <span className="text-zinc-500 text-xs font-mono tabular-nums shrink-0">{formatTime(elapsed)}</span>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-5 space-y-4">
        {/* Oefeningen */}
        {oefeningen.map((ex, exIdx) => {
          const rustSec = rustPerEx[exIdx] ?? DEFAULT_REST;

          // ── Superset rendering ─────────────────────────────────────────────
          if (ex.type === "superset") {
            const numSets = ex.oefeningen[0]?.sets?.length || 1;
            const allSupersetDone = ex.oefeningen.every(sub => sub.sets.every(s => s.done));
            return (
              <div key={exIdx} className={`rounded-2xl border transition-all overflow-hidden ${allSupersetDone ? "border-[#C9A84C]/30 bg-[#C9A84C]/4" : "border-[#C9A84C]/20 bg-[#0f0f0f]"}`}>
                <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: "rgba(201,168,76,0.15)", background: "rgba(201,168,76,0.06)" }}>
                  <Link size={14} style={{ color: GOLD }} />
                  <span className="text-xs font-black uppercase tracking-widest" style={{ color: GOLD }}>Superset</span>
                  <span className="text-zinc-400 text-xs ml-1 truncate">{ex.oefeningen.map(o => o.naam).join(" + ")}</span>
                  <button onClick={() => removeOefening(exIdx)} className="ml-auto text-zinc-600 hover:text-red-400 transition-colors p-1">
                    <Trash2 size={13} />
                  </button>
                </div>
                <div className="px-3 py-3 space-y-3">
                  {Array.from({ length: numSets }).map((_, setIdx) => {
                    const roundDone = ex.oefeningen.every(sub => sub.sets[setIdx]?.done);
                    return (
                      <div key={setIdx} className={`rounded-xl p-3 border transition-all ${roundDone ? "border-[#C9A84C]/20 bg-[#C9A84C]/6" : "border-white/5 bg-white/2"}`}>
                        <div className="flex items-center gap-2 mb-2.5">
                          <span className="text-zinc-500 text-xs font-bold uppercase tracking-widest">Set {setIdx + 1}</span>
                          {roundDone && <Check size={11} style={{ color: GOLD }} />}
                        </div>
                        {ex.oefeningen.map((sub, si) => {
                          const subSet = sub.sets[setIdx] || { reps: 10, kg: "", done: false };
                          const prevSub = prevData[sub.naam];
                          return (
                            <div key={si} className="flex items-center gap-2 mb-1.5">
                              <p className="text-zinc-400 text-xs font-semibold w-20 shrink-0 truncate">{sub.naam}</p>
                              {prevSub && (
                                <span className="text-[10px] shrink-0 tabular-nums font-semibold" style={{ color: GOLD }}>
                                  {prevSub.kg}×{prevSub.reps ?? "–"}
                                </span>
                              )}
                              <div className="flex items-center gap-0.5 shrink-0">
                                <button onPointerDown={e => { e.preventDefault(); updateSupersetSet(exIdx, si, setIdx, "reps", Math.max(0, (subSet.reps || 0) - 1)); }}
                                  className="w-6 h-7 rounded-lg bg-white/6 text-zinc-400 flex items-center justify-center text-sm font-black">−</button>
                                <span className="w-7 text-center text-white font-bold text-sm tabular-nums">{subSet.reps || 0}</span>
                                <button onPointerDown={e => { e.preventDefault(); updateSupersetSet(exIdx, si, setIdx, "reps", (subSet.reps || 0) + 1); }}
                                  className="w-6 h-7 rounded-lg bg-white/6 text-zinc-400 flex items-center justify-center text-sm font-black">+</button>
                              </div>
                              <input type="number" inputMode="decimal" step="0.5"
                                value={subSet.kg}
                                onChange={e => updateSupersetSet(exIdx, si, setIdx, "kg", e.target.value)}
                                placeholder={prevSub?.kg != null ? String(prevSub.kg) : "kg"}
                                data-testid={`superset-kg-${exIdx}-${si}-${setIdx}`}
                                className="w-16 bg-white/5 border border-white/8 rounded-lg px-2 py-1.5 text-white text-xs text-center focus:outline-none focus:border-[#C9A84C]/50"
                              />
                            </div>
                          );
                        })}
                        <button
                          onClick={() => toggleSupersetRound(exIdx, ex.oefeningen.length, setIdx, rustSec)}
                          data-testid={`superset-check-${exIdx}-${setIdx}`}
                          className={`w-full mt-2 py-2 rounded-xl font-bold text-xs transition-all ${roundDone ? "bg-[#C9A84C] text-black" : "bg-white/6 text-zinc-400 hover:bg-white/10"}`}
                        >
                          {roundDone ? "Ronde voltooid" : `Set ${setIdx + 1} klaar`}
                        </button>
                      </div>
                    );
                  })}
                  <button
                    onClick={() => addSupersetSet(exIdx)}
                    data-testid={`add-superset-set-${exIdx}`}
                    className="mt-1 w-full py-2 rounded-xl border border-dashed border-white/8 text-zinc-600 text-xs hover:border-[#C9A84C]/30 hover:text-[#C9A84C] transition-all flex items-center justify-center gap-1"
                  >
                    <Plus size={11} /> Ronde toevoegen
                  </button>
                </div>
              </div>
            );
          }

          // ── Normale oefening rendering ─────────────────────────────────────
          const allSetsDone = ex.sets.every(s => s.done);

          return (
            <div key={exIdx} className={`rounded-2xl border transition-all ${allSetsDone ? "border-[#C9A84C]/30 bg-[#C9A84C]/5" : "border-white/6 bg-[#0f0f0f]"}`}>
              {/* Oefening header */}
              <div className="px-5 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${allSetsDone ? "bg-[#C9A84C] text-black" : "bg-white/5 text-zinc-500"}`}>
                    {allSetsDone ? <Check size={16} /> : <Dumbbell size={15} />}
                  </div>
                  <div>
                    <p className="text-white font-bold text-base">{ex.naam}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-zinc-500 text-xs">{ex.sets.length} sets</span>
                      <span className="text-zinc-700">·</span>
                      <div className="flex items-center gap-1">
                        <Timer size={10} className="text-zinc-600" />
                        <button
                          onPointerDown={e => { e.preventDefault(); setRustPerEx(p => ({ ...p, [exIdx]: Math.max(15, (p[exIdx] ?? DEFAULT_REST) - 15) })); }}
                          data-testid={`rust-minus-${exIdx}`}
                          className="w-5 h-5 rounded bg-white/6 text-zinc-400 text-xs font-black flex items-center justify-center select-none active:bg-white/12"
                        >−</button>
                        <span className="text-zinc-400 text-xs font-mono tabular-nums w-9 text-center select-none" data-testid={`rust-value-${exIdx}`}>
                          {rustSec >= 60
                            ? `${Math.floor(rustSec / 60)}m${rustSec % 60 > 0 ? `${rustSec % 60}s` : ""}`
                            : `${rustSec}s`}
                        </span>
                        <button
                          onPointerDown={e => { e.preventDefault(); setRustPerEx(p => ({ ...p, [exIdx]: Math.min(300, (p[exIdx] ?? DEFAULT_REST) + 15) })); }}
                          data-testid={`rust-plus-${exIdx}`}
                          className="w-5 h-5 rounded bg-white/6 text-zinc-400 text-xs font-black flex items-center justify-center select-none active:bg-white/12"
                        >+</button>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => removeOefening(exIdx)}
                  data-testid={`verwijder-oefening-${exIdx}`}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-zinc-600 hover:text-red-400 hover:bg-red-400/8 transition-all"
                >
                  <Trash2 size={14} />
                </button>
              </div>

              {/* Sets */}
              <div className="px-3 pb-4 space-y-1.5">
                <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-600 mb-1 px-1">
                  <span className="w-4 text-center shrink-0">#</span>
                  {prevData[ex.naam] && <span className="w-14 text-center shrink-0">Vorig</span>}
                  <span className="w-16 text-center shrink-0">Reps</span>
                  <span className="flex-1 text-center">kg</span>
                  <span className="w-8 text-center shrink-0">✓</span>
                </div>

                {ex.sets.map((set, setIdx) => (
                  <div key={setIdx} className={`flex items-center gap-1.5 rounded-xl px-2 py-1.5 transition-all ${set.done ? "bg-[#C9A84C]/8 border border-[#C9A84C]/15" : "bg-white/3 border border-transparent"}`}>
                    <span className="w-4 shrink-0 text-zinc-500 text-xs font-bold text-center">{setIdx + 1}</span>

                    {/* Vorig gewicht */}
                    {prevData[ex.naam] && (
                      <span className="w-14 shrink-0 text-zinc-400 text-xs font-semibold text-center tabular-nums">
                        {prevData[ex.naam].kg != null
                          ? `${prevData[ex.naam].kg}×${prevData[ex.naam].reps ?? "–"}`
                          : "–"}
                      </span>
                    )}

                    {/* Reps stepper */}
                    <div className="flex items-center gap-0.5 shrink-0">
                      <button
                        onPointerDown={e => { e.preventDefault(); updateSet(exIdx, setIdx, "reps", Math.max(0, (set.reps || 0) - 1)); }}
                        className="w-6 h-8 rounded-lg bg-white/6 text-zinc-400 active:text-white flex items-center justify-center text-sm font-black select-none"
                        data-testid={`reps-minus-${exIdx}-${setIdx}`}
                      >−</button>
                      <span className="w-6 text-center text-white font-black text-sm tabular-nums select-none"
                        data-testid={`reps-value-${exIdx}-${setIdx}`}>
                        {set.reps || 0}
                      </span>
                      <button
                        onPointerDown={e => { e.preventDefault(); updateSet(exIdx, setIdx, "reps", (set.reps || 0) + 1); }}
                        className="w-6 h-8 rounded-lg bg-white/6 text-zinc-400 active:text-white flex items-center justify-center text-sm font-black select-none"
                        data-testid={`reps-plus-${exIdx}-${setIdx}`}
                      >+</button>
                    </div>

                    {/* KG stepper */}
                    <div className="flex-1 flex items-center gap-0.5 min-w-0">
                      <button
                        onPointerDown={e => { e.preventDefault(); updateSet(exIdx, setIdx, "kg", String(Math.max(0, Math.round(((parseFloat(set.kg) || 0) - 2.5) * 10) / 10))); }}
                        className="w-7 h-9 rounded-lg flex items-center justify-center text-base font-black select-none shrink-0 transition-all active:scale-90"
                        style={{ background: "rgba(255,255,255,0.07)", color: "#71717a" }}
                        data-testid={`weight-minus-${exIdx}-${setIdx}`}
                      >−</button>
                      <input
                        type="number"
                        inputMode="decimal"
                        placeholder={prevData[ex.naam]?.kg != null ? String(prevData[ex.naam].kg) : "0"}
                        value={set.kg}
                        onChange={e => updateSet(exIdx, setIdx, "kg", e.target.value)}
                        onBlur={e => handleWeightBlur(exIdx, setIdx, e.target.value)}
                        className="flex-1 min-w-0 bg-[#111] border border-white/8 rounded-xl py-2 text-white text-base text-center font-black focus:outline-none focus:border-[#C9A84C]/60 focus:bg-[#0f0c02] tabular-nums transition-colors"
                        data-testid={`weight-input-${exIdx}-${setIdx}`}
                      />
                      <button
                        onPointerDown={e => { e.preventDefault(); updateSet(exIdx, setIdx, "kg", String(Math.round(((parseFloat(set.kg) || 0) + 2.5) * 10) / 10)); }}
                        className="w-7 h-9 rounded-lg flex items-center justify-center text-base font-black select-none shrink-0 transition-all active:scale-90"
                        style={{ background: "rgba(255,255,255,0.07)", color: "#71717a" }}
                        data-testid={`weight-plus-${exIdx}-${setIdx}`}
                      >+</button>
                    </div>

                    {/* Done knop */}
                    <button
                      onClick={() => toggleSet(exIdx, setIdx, rustSec, oefeningen[exIdx]?.naam, parseFloat(set.kg || "0") || 0)}
                      data-testid={`toggle-set-${exIdx}-${setIdx}`}
                      className={`w-8 h-9 rounded-lg shrink-0 flex items-center justify-center transition-all active:scale-90 ${set.done ? "text-black" : "bg-white/5 text-zinc-600"}`}
                      style={set.done ? { background: GOLD } : {}}
                    >
                      {set.done ? <Check size={13} /> : <X size={11} />}
                    </button>

                    {/* Set verwijderen */}
                    {ex.sets.length > 1 && (
                      <button
                        onPointerDown={e => { e.preventDefault(); removeSet(exIdx, setIdx); }}
                        data-testid={`remove-set-${exIdx}-${setIdx}`}
                        className="w-6 h-9 rounded-lg shrink-0 flex items-center justify-center text-zinc-700 hover:text-red-400 active:text-red-400 transition-colors select-none"
                        title="Set verwijderen"
                      >
                        <Trash2 size={11} />
                      </button>
                    )}
                  </div>
                ))}

                <button
                  onClick={() => addSet(exIdx)}
                  data-testid={`add-set-${exIdx}`}
                  className="mt-2 w-full py-2 rounded-xl border border-dashed border-white/8 text-zinc-600 text-xs hover:border-[#C9A84C]/30 hover:text-[#C9A84C] transition-all flex items-center justify-center gap-1"
                >
                  <Plus size={11} /> Set toevoegen
                </button>
              </div>
            </div>
          );
        })}

        {/* Lege staat */}
        {oefeningen.length === 0 && (
          <div className="rounded-2xl border-2 border-dashed border-white/8 p-10 text-center">
            <Dumbbell size={32} className="mx-auto text-zinc-700 mb-3" />
            <p className="text-zinc-500 text-sm font-semibold mb-1">Nog geen oefeningen</p>
            <p className="text-zinc-700 text-xs">Voeg je eerste oefening toe om te beginnen.</p>
          </div>
        )}

        {/* Oefening toevoegen knop */}
        <div className="flex flex-col gap-2">
          <button
            onClick={() => setShowZoeker(true)}
            data-testid="vrije-logger-voeg-oefening-toe"
            className="w-full py-3.5 rounded-2xl border-2 border-dashed border-[#C9A84C]/25 text-[#C9A84C] font-bold text-sm hover:border-[#C9A84C]/50 hover:bg-[#C9A84C]/5 transition-all flex items-center justify-center gap-2"
          >
            <Plus size={16} /> Oefening toevoegen
          </button>
          <button
            onClick={() => setShowSupersetPicker(true)}
            data-testid="vrije-logger-voeg-superset-toe"
            className="w-full py-3 rounded-2xl border border-dashed border-[#C9A84C]/15 text-zinc-500 font-semibold text-sm hover:border-[#C9A84C]/30 hover:text-[#C9A84C] transition-all flex items-center justify-center gap-2"
          >
            <Link size={14} /> Superset toevoegen
          </button>
        </div>

        {/* Afronding */}
        <div className="pt-2">
          <button
            onClick={handleFinish}
            disabled={saving || oefeningen.length === 0}
            data-testid="vrije-logger-afronden"
            className={`w-full py-4 rounded-2xl font-black text-base uppercase tracking-wide transition-all flex items-center justify-center gap-2
              ${allDone && oefeningen.length > 0 ? "text-black shadow-lg active:scale-95" : "bg-white/5 text-zinc-500"}`}
            style={allDone && oefeningen.length > 0 ? { background: GOLD } : {}}
          >
            {saving ? "Opslaan..."
              : oefeningen.length === 0 ? "Voeg oefeningen toe om te starten"
              : allDone ? <><CheckCircle size={18} /> Training voltooid!</>
              : `Nog ${resterendeSets} ${resterendeSets === 1 ? "set" : "sets"} te gaan`}
          </button>
          {saveError && (
            <p className="text-center text-red-400 text-xs mt-3">Opslaan mislukt — probeer opnieuw.</p>
          )}
        </div>
      </div>

      {/* Rusttimer */}
      <RestTimer
        endTime={restTimer?.endTime}
        total={restTimer?.total}
        onSkip={() => setRestTimer(null)}
        onAdd={(extra) => setRestTimer(p => p ? { ...p, endTime: p.endTime + extra * 1000 } : null)}
      />

      <PrMelding melding={prMelding} onDismiss={() => setPrMelding(null)} />

      {/* Oefening zoeker modal */}
      {showZoeker && (
        <OefeningZoekerModal
          token={token}
          bestaandeNamen={oefeningen.map(e => e.naam)}
          onAdd={addOefening}
          onClose={() => setShowZoeker(false)}
        />
      )}
      {showSupersetPicker && (
        <VrijeSupersetPicker
          token={token}
          onSelect={(naam1, naam2) => addSuperset(naam1, naam2)}
          onClose={() => setShowSupersetPicker(false)}
        />
      )}
    </div>
  );
}
